import { useState } from "react";
import { Heart } from "lucide-react";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { Vow, VowCategory } from "@/data/vows";
import { VOW_CATEGORIES } from "@/data/vows";

interface VowCardProps {
  vow: Vow;
  isFavorite: boolean;
  onToggleFavorite: (vowId: number) => void;
  onClick: (vow: Vow) => void;
  showCategory?: boolean;
}

const CARD_GRADIENTS: Record<VowCategory, string> = {
  "refuge-commitments":
    "from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200/60 dark:border-blue-800/40",
  "ten-virtues":
    "from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border-emerald-200/60 dark:border-emerald-800/40",
  "bodhisattva-root":
    "from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 border-yellow-200/60 dark:border-yellow-800/40",
  "bodhisattva-secondary":
    "from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20 border-purple-200/60 dark:border-purple-800/40",
  "bodhisattva-helping":
    "from-pink-50 to-rose-50 dark:from-pink-900/20 dark:to-rose-900/20 border-pink-200/60 dark:border-pink-800/40",
  "bodhisattva-enlightenment":
    "from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 border-orange-200/60 dark:border-orange-800/40",
  "bodhisattva-black-white":
    "from-slate-50 to-gray-50 dark:from-slate-900/20 dark:to-gray-900/20 border-slate-200/60 dark:border-slate-800/40",
  "lay-vows":
    "from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-amber-200/60 dark:border-amber-800/40",
};

const CATEGORY_BADGE: Record<VowCategory, string> = {
  "refuge-commitments": "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  "ten-virtues": "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  "bodhisattva-root": "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300",
  "bodhisattva-secondary": "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300",
  "bodhisattva-helping": "bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300",
  "bodhisattva-enlightenment": "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
  "bodhisattva-black-white": "bg-slate-100 text-slate-700 dark:bg-slate-900/40 dark:text-slate-300",
  "lay-vows": "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
};

const HEART_COLORS: Record<VowCategory, string> = {
  "refuge-commitments": "text-blue-500",
  "ten-virtues": "text-emerald-500",
  "bodhisattva-root": "text-yellow-500",
  "bodhisattva-secondary": "text-purple-500",
  "bodhisattva-helping": "text-pink-500",
  "bodhisattva-enlightenment": "text-orange-500",
  "bodhisattva-black-white": "text-slate-500",
  "lay-vows": "text-amber-500",
};

const DESCRIPTION_LIMIT = 180;

function getCategoryLabel(category: VowCategory): string {
  return VOW_CATEGORIES.find((c) => c.id === category)?.labelShort ?? category;
}

export function VowCard({
  vow,
  isFavorite,
  onToggleFavorite,
  onClick,
  showCategory = false,
}: VowCardProps) {
  const [expanded, setExpanded] = useState(false);
  const isLong = vow.de.description.length > DESCRIPTION_LIMIT;
  const displayText =
    !expanded && isLong
      ? `${vow.de.description.slice(0, DESCRIPTION_LIMIT)}…`
      : vow.de.description;

  return (
    <Card
      className={cn(
        "overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-md bg-gradient-to-br",
        CARD_GRADIENTS[vow.category]
      )}
      onClick={() => onClick(vow)}
    >
      <div className="p-4 space-y-2.5">
        {/* Category badge (only in "Alle" view) */}
        {showCategory && (
          <span
            className={cn(
              "inline-block text-xs font-medium px-2 py-0.5 rounded-full",
              CATEGORY_BADGE[vow.category]
            )}
          >
            {getCategoryLabel(vow.category)}
          </span>
        )}

        {/* Title row */}
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-base font-semibold leading-snug text-gray-900 dark:text-foreground break-words min-w-0">
            <span className="text-gray-400 dark:text-muted-foreground font-normal mr-1.5">
              {vow.id}.
            </span>
            {vow.de.title}
          </h3>
          <button
            className="shrink-0 mt-0.5 p-1 -mr-1 rounded-full transition-colors hover:bg-black/5 dark:hover:bg-white/10"
            onClick={(e) => {
              e.stopPropagation();
              onToggleFavorite(vow.id);
            }}
            aria-label={isFavorite ? "Aus Favoriten entfernen" : "Zu Favoriten hinzufügen"}
          >
            <Heart
              className={cn(
                "h-4.5 w-4.5 transition-all",
                isFavorite
                  ? `fill-current text-red-500`
                  : `text-gray-300 dark:text-muted-foreground/50 hover:${HEART_COLORS[vow.category]}`
              )}
            />
          </button>
        </div>

        {/* Description */}
        <p className="text-sm text-gray-600 dark:text-muted-foreground leading-relaxed break-words">
          {displayText}
        </p>

        {/* Expand/Collapse toggle */}
        {isLong && (
          <button
            className="text-xs font-medium text-vajra-blue dark:text-vajra-cyan hover:underline"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((p) => !p);
            }}
          >
            {expanded ? "Weniger anzeigen" : "Mehr anzeigen"}
          </button>
        )}

        {/* Sanskrit subtitle */}
        {vow.titleSanskrit && (
          <p className="text-xs text-gray-400 dark:text-muted-foreground/50 italic break-words">
            {vow.titleSanskrit}
          </p>
        )}
      </div>
    </Card>
  );
}
